/**
 * Viewport math. Pure: same inputs => same output, no DOM, no canvas.
 *
 * Coordinates here are in screen pixels (not sim subpixels) because the
 * renderer ultimately deals in pixels and asking it to convert twice is
 * silly. The helper `subpixelToPixel` is the bridge.
 */

import { SUBPIXELS_PER_TILE } from '../sim/fixed.ts';

export const TILE_PX = 32;

export type Viewport = {
  /** Top-left x of the visible area in maze pixels. */
  readonly x: number;
  /** Top-left y of the visible area in maze pixels. */
  readonly y: number;
  /** Width of the visible area in pixels. */
  readonly width: number;
  /** Height of the visible area in pixels. */
  readonly height: number;
};

export function subpixelToPixel(sub: number): number {
  // SUBPIXELS_PER_TILE / TILE_PX = 256 / 32 = 8. Integer divide.
  return Math.floor((sub * TILE_PX) / SUBPIXELS_PER_TILE);
}

export function tileToPixel(tile: number): number {
  return tile * TILE_PX;
}

/**
 * Center the viewport on the player and clamp to the maze. If the maze is
 * smaller than the viewport, the viewport hangs off the bottom-right
 * (top-left is 0) so the visible maze sits at the top-left of the canvas.
 */
export function computeViewport(
  playerSubX: number,
  playerSubY: number,
  mazeWidthTiles: number,
  mazeHeightTiles: number,
  viewportWidthPx: number,
  viewportHeightPx: number,
): Viewport {
  const playerPx = subpixelToPixel(playerSubX);
  const playerPy = subpixelToPixel(playerSubY);
  const mazeWidthPx = mazeWidthTiles * TILE_PX;
  const mazeHeightPx = mazeHeightTiles * TILE_PX;

  let x = playerPx - Math.floor(viewportWidthPx / 2);
  let y = playerPy - Math.floor(viewportHeightPx / 2);

  // Clamp so we never show past the maze edge.
  const maxX = Math.max(0, mazeWidthPx - viewportWidthPx);
  const maxY = Math.max(0, mazeHeightPx - viewportHeightPx);
  if (x < 0) x = 0;
  if (y < 0) y = 0;
  if (x > maxX) x = maxX;
  if (y > maxY) y = maxY;

  return { x, y, width: viewportWidthPx, height: viewportHeightPx };
}
